import React, { useEffect, useState } from "react";
import "./trails.scss";
import SearchBar from "../../components/searchBar/searchBar";
import { useTranslation } from "react-i18next";
import CustomToggleButton from "../../components/customToggleButton/customToggleButton";
import { Label } from "reactstrap";
import CustomSelect from "../../components/customSelect/customSelect";
// import trails from '../../api/trails.json';
import emptyTrail from "../../assets/images/emptyTrail.svg";
import noDataFound from "../../assets/images/noDataFountTrail.svg";
import useWindowDimension from "../../components/hooks/useWindowDimension";
import CustomButton from "../../components/customButton/customButton";
import { useLocation, useNavigate } from "react-router-dom";
import imagePlaceholder from "../../assets/images/tottPlaceholder.svg";
import { Image } from "react-bootstrap";
import axios from "axios";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/features/toast/toastSlice";
import { TRAILS_LIST } from "../../api/endPoint";
import "bootstrap/dist/css/bootstrap.css";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import dotenv from "dotenv";
import { getCloudinaryFeaturedImageUrl } from "../../util/cloudinaryUpload";
import { Helmet } from "react-helmet";

interface Trail {
  id: string;
  title: string;
  description: string;
  status: "Active" | "Disabled";
  trail_type: string;
  route_type: string;
  difficulty: string;
  length: number;
  ascent: number;
  time: string;
  average_time_hours: number;
  average_time_minutes: number;
  disable: string;
  towns: string;
  gpxStartLat: string;
  gpxStartLng: string;
  gpXFile: string;
  featured_image: string;
  startPoint: {
    latitude: number;
    longitude: number;
  };
  endPoint: {
    latitude: number;
    longitude: number;
  };
}

const Trails: React.FC = () => {
  const { t } = useTranslation();
  const dimensions = useWindowDimension();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Retrieve stored values from localStorage or set default values
  const storedTrailType = localStorage.getItem("TrailType");
  const storedRouteType = localStorage.getItem("RouteType");
  const storedDifficulty = localStorage.getItem("Difficulty");
  const storedSortBy = localStorage.getItem("SortBy");

  const [trailSelectedOption, setTrailSelectedOption] = useState<{
    label: string;
    value: string;
  } | null>(
    storedTrailType ? { label: storedTrailType, value: storedTrailType } : null
  );
  const [routeSelectedOption, setRouteSelectedOption] = useState<{
    label: string;
    value: string;
  } | null>(
    storedRouteType ? { label: storedRouteType, value: storedRouteType } : null
  );
  const [difficultySelectedOption, setDifficultySelectedOption] = useState<{
    label: string;
    value: string;
  } | null>(
    storedDifficulty
      ? { label: storedDifficulty, value: storedDifficulty }
      : null
  );
  const [sortSelectedOption, setSortSelectedOption] = useState<{
    label: string;
    value: string;
  } | null>(storedSortBy ? { label: storedSortBy, value: storedSortBy } : null);

  const [activePage, setActivePage] = useState(1); // Track the active page

  const storedSearch = localStorage.getItem("TrailSearch");
  const storedToggleValue = localStorage.getItem("TrailToggle");
  const [checked, setChecked] = useState<boolean>(storedToggleValue === "true");
  const [searchQuery, setSearchQuery] = useState(storedSearch || ""); // State for search input
  const [loading, setLoading] = useState(true);

  const [searchBarHeight, setSearchBarHeight] = useState<number>(0);
  const [filterSectionHeight, setFilterSectionHeight] = useState<number>(0);

  const [trails, setTrails] = useState<{ data: Trail[] }>({ data: [] });

  const [filteredTrails, setFilteredTrails] = useState<Trail[]>([]);
  // const isAdmin = localStorage.getItem('isAdmin') === 'true'; // Adjust based on how you store user role

  const fetchTrails = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      // For admin users, fetch both Active and Disabled trails
      // For non-admin users, API will automatically filter to only Active trails
      const statusParam = "Active,Disabled";

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${TRAILS_LIST}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            status: statusParam,
          },
        }
      );

      setTrails(response.data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        dispatch(
          showToast({
            show: true,
            message: "Please login to view trails",
            type: "error",
          })
        );
        navigate("/login");
      } else {
        dispatch(
          showToast({
            show: true,
            message: error.response?.data?.message || "Failed to fetch trails",
            type: "error",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrails();
  }, []);

  const handleImageError = (e: any) => {
    e.target.src = imagePlaceholder; // Set the image to placeholder if it fails to load
  };

  useEffect(() => {
    setInterval(() => {
      setLoading(false);
    }, 3000);
  }, []);
  useEffect(() => {
    const searchBarHeightElement = document.getElementsByClassName(
      "searchSection"
    )[0] as HTMLElement;
    const filterSectionHeightElement = document.getElementsByClassName(
      "filterSection"
    )[0] as HTMLElement;

    if (searchBarHeightElement) {
      setSearchBarHeight(searchBarHeightElement.offsetHeight);
    }
    if (filterSectionHeightElement) {
      setFilterSectionHeight(filterSectionHeightElement.offsetHeight);
    }
  }, []);

  const trailsPerPage = 20;

  // Save the toggle value to localStorage and update the state
  const onToggleSwitch = () => {
    const newCheckedValue = !checked;
    setChecked(newCheckedValue);
    localStorage.setItem("TrailToggle", JSON.stringify(newCheckedValue));

    if (newCheckedValue) {
      // Make sure we're case-insensitive when checking status
      const disabledTrails = trails?.data?.filter(
        (trail) => trail.status?.toLowerCase() === "disabled"
      );
      setFilteredTrails(disabledTrails);
    } else {
      // Show all trails when toggle is off
      setFilteredTrails(trails?.data);
    }
  };

  const handleSearchChange = (event: string) => {
    setSearchQuery(event);
  };

  // Save dropdown values to localStorage
  const handleTrailChange = (selected: any) => {
    setTrailSelectedOption(selected);
    localStorage.setItem("TrailType", selected ? selected.value : "");
  };

  const handleRouteChange = (selected: any) => {
    setRouteSelectedOption(selected);
    localStorage.setItem("RouteType", selected ? selected.value : "");
  };

  const handleDifficultyChange = (selected: any) => {
    setDifficultySelectedOption(selected);
    localStorage.setItem("Difficulty", selected ? selected.value : "");
  };

  const handleSortChange = (selected: any) => {
    setSortSelectedOption(selected);
    localStorage.setItem("SortBy", selected ? selected.value : "");
  };

  const onFilterClear = () => {
    setTrailSelectedOption(null);
    setRouteSelectedOption(null);
    setDifficultySelectedOption(null);

    localStorage.setItem("TrailType", "");
    localStorage.setItem("RouteType", "");
    localStorage.setItem("Difficulty", "");
    localStorage.setItem("SortBy", "");
  };

  const applyFilters = () => {
    // Check if trails.data exists before filtering
    let updatedTrails = trails?.data || [];

    if (searchQuery?.length >= 3) {
      updatedTrails = updatedTrails.filter((trail: { title: string }) =>
        trail.title?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply the status filter if the switch is checked
    if (checked) {
      updatedTrails = updatedTrails.filter(
        (trail) => trail.status?.toLowerCase() === "disabled"
      );
    }

    if (trailSelectedOption && trailSelectedOption.value !== "All") {
      updatedTrails = updatedTrails.filter(
        (trail) => trail.trail_type === trailSelectedOption.value
      );
    }

    if (routeSelectedOption && routeSelectedOption.value !== "All") {
      updatedTrails = updatedTrails.filter(
        (trail: { route_type: string }) =>
          trail.route_type === routeSelectedOption.value
      );
    }

    if (difficultySelectedOption && difficultySelectedOption.value !== "All") {
      updatedTrails = updatedTrails.filter(
        (trail: { difficulty: string }) =>
          trail.difficulty === difficultySelectedOption.value
      );
    }

    if (sortSelectedOption) {
      updatedTrails = [...updatedTrails]; // Clone the array to avoid mutation
      switch (sortSelectedOption.value) {
        case "A-Z":
          updatedTrails.sort((a: { title: string }, b: { title: any }) =>
            a.title.localeCompare(b.title)
          );
          break;
        case "Z-A":
          updatedTrails.sort((a: { title: any }, b: { title: string }) =>
            b.title.localeCompare(a.title)
          );
          break;
        case "Shortest to Longest":
          updatedTrails.sort((a: Trail, b: Trail) => a?.length - b?.length);
          break;
        case "Longest to Shortest":
          updatedTrails.sort((a: Trail, b: Trail) => b?.length - a?.length);
          break;
        case "High to Low (Ascent)":
          updatedTrails.sort(
            (a: { ascent: number }, b: { ascent: number }) =>
              b.ascent - a.ascent
          );
          break;
        case "Low to High (Ascent)":
          updatedTrails?.sort(
            (a: { ascent: number }, b: { ascent: number }) =>
              a?.ascent - b?.ascent
          );
          break;
        default:
          break;
      }
    }

    setFilteredTrails(updatedTrails);
  };

  // Update the useEffect that handles filter changes
  useEffect(() => {
    if (trails?.data) {
      // Only reset to page 1 if filters have changed
      const storedPage = localStorage.getItem("activePage");
      if (!storedPage) {
        setActivePage(1);
        localStorage.setItem("activePage", "1");
      } else {
        setActivePage(parseInt(storedPage));
      }
      applyFilters();
    }
  }, [
    searchQuery,
    checked,
    trailSelectedOption,
    routeSelectedOption,
    difficultySelectedOption,
    sortSelectedOption,
    trails,
  ]);

  // Update the handlePageChange function to ensure proper storage
  const handlePageChange = (pageIndex: number) => {
    const newPage = pageIndex + 1;
    setActivePage(newPage);
    localStorage.setItem("activePage", newPage.toString());
    // Scroll to top of trail list
    const trailListContainer = document.querySelector(".customScroll");
    if (trailListContainer) {
      trailListContainer.scrollTop = 0;
    }
  };

  // Add a cleanup effect to clear pagination when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem("activePage");
    };
  }, []);

  const trailOptions = [
    { label: "All", value: "All" },
    { label: "Walk", value: "Walk" },
    { label: "Walk + Run", value: "Walk + Run" },
    { label: "Run", value: "Run" },
    { label: "Cycle", value: "Cycle" },
    { label: "Ride", value: "Ride" },
  ];

  const routeOptions = [
    { label: "All", value: "All" },
    { label: "Loop", value: "Loop" },
    { label: "Out and back", value: "Out and back" },
    { label: "Point to point", value: "Point to point" },
  ];

  const difficultyOptions = [
    { label: "All", value: "All" },
    { label: "Easy", value: "Easy" },
    { label: "Intermediate", value: "Intermediate" },
    { label: "Advanced", value: "Advanced" },
  ];

  const sortOptions = [
    { label: "A-Z", value: "A-Z" },
    { label: "Z-A", value: "Z-A" },
    { label: "Shortest to Longest", value: "Shortest to Longest" },
    { label: "Longest to Shortest", value: "Longest to Shortest" },
    { label: "High to Low (Ascent)", value: "High to Low (Ascent)" },
    { label: "Low to High (Ascent)", value: "Low to High (Ascent)" },
  ];

  // Calculate the total number of pages
  const totalPages = Math.ceil(filteredTrails?.length / trailsPerPage);

  const currentTrails = filteredTrails?.slice(
    (activePage - 1) * trailsPerPage,
    activePage * trailsPerPage
  );
  const onCardClick = (trail: any) => {
    navigate(`/trails/trail-detail/${trail.id}`, {
      state: {
        trailData: trail,
        currentPage: activePage,
      },
    });
  };

  const location = useLocation();
  useEffect(() => {
    if (location.state?.currentPage) {
      setActivePage(location.state?.currentPage);
      localStorage.setItem(
        "activePage",
        location.state?.currentPage.toString()
      );
    }
  }, []);

  const formatTime = (hours: number, minutes: number): string => {
    return `${hours} H ${minutes} Min`;
  };

  const getPaginationText = () => {
    const start = (activePage - 1) * trailsPerPage + 1;
    const end = Math.min(activePage * trailsPerPage, filteredTrails.length);
    return `Showing ${start}–${end} out of ${filteredTrails.length}`;
  };

  useEffect(() => {
    const location = window.location;
    const state = (location as any).state;

    if (state?.currentPage) {
      setActivePage(state.currentPage);
      localStorage.setItem("activePage", state.currentPage.toString());
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>All Trails | Town of 1000 Trails Admin Panel</title>
        <meta
          name="description"
          content="View, search, and manage all current and past trails available in the app."
        />
      </Helmet>
      <div
        className={
          currentTrails?.length === 0
            ? "main-container container emptyDataContainer tarilListContainer"
            : trails?.data?.length > 0
            ? "main-container container emptyDataContainer tarilListContainer"
            : "main-container container tarilListContainer"
        }
      >
        <div className="pageTopbar">
          <div className="searchSection">
            <SearchBar
              storedValue="TrailSearch"
              loading={loading}
              placeholder={t("SEARCH_BY_TITLE")}
              onChange={(e: any) => {
                handleSearchChange(e);
              }}
              onClear={() => setSearchQuery("")}
            />
            <div
              className={
                loading ? "disableToggle placeholder-glow" : "disableToggle"
              }
            >
              {loading ? (
                <span className=" placeholder w-25"></span>
              ) : (
                <CustomToggleButton
                  checked={checked}
                  id={""}
                  onCheckChange={onToggleSwitch}
                />
              )}
              {loading ? (
                <span className="placeholder w-75 ms-2"></span>
              ) : (
                <Label>{t("VIEW_DISABLED_ONLY")}</Label>
              )}
            </div>
          </div>
          <div className="filterSection">
            <div className="leftPart placeholder-glow">
              <span className={loading ? "placeholder" : ""}>
                {" "}
                {t("FILTER")}{" "}
              </span>
              {loading ? (
                <div className="placeholder-glow">
                  <span className="selectBox filterSelect placeholder w-100"></span>
                </div>
              ) : (
                <CustomSelect
                  value={trailSelectedOption || null}
                  onChange={handleTrailChange}
                  closeMenuOnSelect={true}
                  placeholder={t("TRAIL_TYPE")}
                  isClearable
                  options={trailOptions}
                  isSearchable={false}
                  hideSelectedOptions={false}
                  className="filterSelect"
                  classNamePrefix="react-select"
                />
              )}
              {loading ? (
                <div className="placeholder-glow">
                  <span className="selectBox filterSelect placeholder w-100"></span>
                </div>
              ) : (
                <CustomSelect
                  value={routeSelectedOption}
                  onChange={handleRouteChange}
                  closeMenuOnSelect={true}
                  placeholder={t("ROUTE_TYPE")}
                  isClearable
                  options={routeOptions}
                  isSearchable={false}
                  hideSelectedOptions={false}
                  className="filterSelect"
                  classNamePrefix="react-select"
                />
              )}
              {loading ? (
                <div className="placeholder-glow">
                  <span className="selectBox filterSelect placeholder w-100"></span>
                </div>
              ) : (
                <CustomSelect
                  value={difficultySelectedOption}
                  onChange={handleDifficultyChange}
                  closeMenuOnSelect={true}
                  placeholder={t("DIFFICULTY")}
                  isClearable
                  options={difficultyOptions}
                  isSearchable={false}
                  hideSelectedOptions={false}
                  className="filterSelect"
                  classNamePrefix="react-select"
                />
              )}
              {(trailSelectedOption ||
                routeSelectedOption ||
                difficultySelectedOption) && (
                <button
                  onClick={() => {
                    onFilterClear();
                  }}
                  className="clearBtn btn-transparent"
                >
                  {t("CLEAR")}
                </button>
              )}
            </div>
            <div className="rightPart">
              {loading ? (
                <div className="placeholder-glow">
                  <span className="selectBox filterSelect placeholder w-100"></span>
                </div>
              ) : (
                <CustomSelect
                  value={sortSelectedOption}
                  onChange={handleSortChange}
                  closeMenuOnSelect={true}
                  placeholder={t("SORT_BY")}
                  isClearable
                  options={sortOptions}
                  isSearchable={false}
                  hideSelectedOptions={false}
                  className="filterSelect"
                  classNamePrefix="react-select"
                />
              )}
            </div>
          </div>
        </div>
        <div
          className="customScroll"
          style={{
            height:
              dimensions.width > 1280
                ? dimensions.height -
                  searchBarHeight -
                  filterSectionHeight -
                  200
                : dimensions.height -
                  searchBarHeight -
                  filterSectionHeight -
                  250,
            overflow: "auto",
          }}
        >
          {loading ? (
            <div className="mainContent">
              <div className="cardWrapper">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="whiteCard placeholder-glow">
                    <div className="cardImg">
                      <span className="placeholder w-100"></span>
                    </div>
                    <div className="cardContent">
                      <div className="cardTitlePlaceholder mb-3">
                        <span className="placeholder col-8"></span>
                        <span className="placeholder col-12"></span>
                      </div>
                      <div className="contentBtm">
                        <div className="dataWrapper">
                          <div className="label">
                            <span className="placeholder col-12"></span>
                          </div>
                          <div className="data">
                            <span className="placeholder col-12"></span>
                          </div>
                        </div>
                        <div className="dataWrapper">
                          <div className="label">
                            <span className="placeholder col-12"></span>
                          </div>
                          <div className="data">
                            <span className="placeholder col-12"></span>
                          </div>
                        </div>
                        <div className="dataWrapper">
                          <div className="label">
                            <span className="placeholder col-12"></span>
                          </div>
                          <div className="data">
                            <span className="placeholder col-12"></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : trails?.data?.length === 0 ? (
            <div className="emptyData">
              <div className="emptyImage">
                <Image src={emptyTrail} alt="No Trail" />
              </div>
              <div className="emptyContent">
                <h5 className="title mb-3">{t("NO_TRAIL")}</h5>
                <div className="description mb-3">
                  {t("LOOKS_LIKE_NO_TRAILS_ARE_ADDED_CURRENTLY")}
                </div>
                <div className="description mb-3">
                  {t("PLEASE_ADD_TRAIL_BY_CLICKING_ON_THE_BELOW_BUTTON")}
                </div>
                <CustomButton
                  className="btn btn-primary text-white text-center justify-content-center btnWithicon"
                  onClick={() => navigate("/add-trail")}
                  icon={<i className="icon-plus-square-fill"></i>}
                  title={t("ADD_TRAIL")}
                />
              </div>
            </div>
          ) : currentTrails?.length === 0 ? (
            <div className="noDataFont">
              <div className="emptyImage">
                <Image src={noDataFound} alt="No Trail" />
              </div>
              <div className="emptyContent">
                <h5 className="title mb-3">{t("NO_SEARCH_DATA_FOUND")}</h5>
                <div className="description mb-3">
                  {t("NO_DATA_FOUND_SEARCH_DESCRIPTION")}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="mainContent">
                <div className="cardWrapper">
                  {currentTrails?.map((item: any, index: number) => (
                    <div
                      onClick={() => onCardClick(item)}
                      key={index}
                      className={
                        item.status === "Disabled"
                          ? "whiteCard trailCard disable"
                          : "whiteCard trailCard"
                      }
                    >
                      <div className="cardImg">
                        <Image
                          src={
                            item.featured_image
                              ? getCloudinaryFeaturedImageUrl(
                                  item.featured_image
                                )
                              : imagePlaceholder
                          }
                          alt={item.title || "Trail Image"}
                          onLoad={() => setLoading(false)}
                          onError={handleImageError}
                        />
                      </div>
                      <div className="cardContent">
                        <OverlayTrigger
                          delay={{ hide: 100, show: 100 }}
                          overlay={(props) => (
                            <Tooltip {...props}>
                              {item.title || "No Title"}
                            </Tooltip>
                          )}
                          placement="bottom"
                        >
                          <div className="cardTitle">
                            {item.title || "No Title"}
                          </div>
                        </OverlayTrigger>
                        <div className="contentBtm">
                          <div className="dataWrapper">
                            <div className="label">{t("DIFFICULTY")}</div>
                            <div
                              className={
                                item.difficulty === "Intermediate"
                                  ? "data cardTextYellow"
                                  : item.difficulty === "Advanced"
                                  ? "cardTextRed data"
                                  : "cardTextGreen data"
                              }
                            >
                              {item.difficulty.toUpperCase() || "-"}
                            </div>
                          </div>
                          <div className="dataWrapper">
                            <div className="label">{t("LENGTH")}</div>
                            <div className="data">
                              {item?.length || "-"} {t("MILES")}
                            </div>
                          </div>
                          <div className="dataWrapper">
                            <div className="label">{t("TIME")}</div>
                            <div className="data">
                              {formatTime(
                                item.average_time_hours,
                                item.average_time_minutes
                              ) || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={
                  loading
                    ? "paginationSection placeholder-glow"
                    : "paginationSection"
                }
              >
                {loading ? (
                  <span className="pageNum placeholder col-12"></span>
                ) : (
                  <span className="pageNum">{getPaginationText()}</span>
                )}
                <nav aria-label="Page navigation">
                  <ul className="pagination">
                    {Array.from({ length: totalPages }).map((_, pageIndex) => (
                      <li
                        key={pageIndex}
                        className={`page-item ${
                          activePage === pageIndex + 1 ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault(); // Prevent default navigation behavior
                          if (!loading) {
                            handlePageChange(pageIndex); // Only set active page if not loading
                          }
                        }}
                      >
                        <span className="page-link">
                          {loading ? (
                            <span className="placeholder col-10"></span>
                          ) : (
                            pageIndex + 1
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Trails;
